import { DISABLED_PLUGINS, ENABLED_PLUGINS, PLUGIN_NAME } from './config';
import { getHashValue, getParamURL } from '../../helpers/js-utils';
import { TESTER_PARAMS } from './Tester/config';
import { IDetection } from '../../uanc.types';
import DebugUANC from '../../core/version/debug';
import { AD_CONSOLE, AD_CONSOLE_STORE, STATE_SERVICE } from '../../configs/constants';

/**
 * Zwraca id testu przekazanego w URL
 *
 * @param {string} url - url serwisu
 * @returns {string | null} ID testu
 */
export const getTestId = (url: string): string | null => {
    return (
        getHashValue(url, TESTER_PARAMS.TESTER_PARAM_ITG) ??
        getHashValue(url, TESTER_PARAMS.TESTER_PARAM_V2) ??
        getHashValue(url, TESTER_PARAMS.TESTER_PARAM) ??
        getParamURL(url, 'adtester_2023')
    );
};

/**
 * Weryfikuje czy plugin AdTester może zostać zainicjowany (na podstawie URL - hash lub parametru).
 *
 * @param {string} url - url serwisu
 * @param {boolean} isNoAd - czy reklamy są wyłączone - wyświetlana informacja
 * @returns {boolean} Czy Tester Plugin powinien być ładowany
 */
export const isTesterPluginToEmbed = (url: string, isNoAd: boolean): boolean => {
    const isTestId = !!getTestId(url);
    if (isTestId && isNoAd) {
        alert('Tester nie może zostać zainicjowany ponieważ reklamy zostały wyłączone przez redaktora.');
    }
    return !isNoAd && isTestId;
};

/**
 * Weryfikuje czy plugin AdConsole może zostać zainicjowany.
 *
 * @returns {boolean} zwracja informację czy AdConsole Plugin powinien być ładowany
 */
export const isAdConsolePluginToEmbed = (): boolean => {
    if (localStorage.getItem(AD_CONSOLE_STORE)) return true;

    const params = new URLSearchParams(window.location.search);
    return params.has(AD_CONSOLE.UANC) || params.has(AD_CONSOLE.VUANC);
};

/**
 * Zwraca nazwy pluginów, które mogą być załadowane.
 * Weryfikacja czy plugin jest wykluczony z użycia w danym serwisie.
 *
 * @returns {PLUGIN_NAME[]} Nazwy pluginów do osadzenia
 */
export const getPluginsNamesToEmbed = (): PLUGIN_NAME[] => {
    const { Ad } = window.Inpl;
    const { host, url } = Ad.detection as IDetection;

    return ENABLED_PLUGINS.filter((pluginName) => {
        if (pluginName === PLUGIN_NAME.TESTER) return isTesterPluginToEmbed(url, Ad.settings?.noAdvertisement || false);
        if (pluginName === PLUGIN_NAME.AD_CONSOLE) return isAdConsolePluginToEmbed();

        const isPluginDisabled = DISABLED_PLUGINS.find((disabledPlugin) => {
            return disabledPlugin.host === host && disabledPlugin.plugin.includes(pluginName);
        });
        return !isPluginDisabled;
    });
};

/**
 * Ładuje odpowiednie pluginy.
 * Aktualizuje informacje o testerze. Ustawia informacje w serwisie (services.TesterService) i w stanie uanca (state.TesterServiceState).
 * W przypadku rzucenia wyjątku przez ktoryś plugin działanie uanca nie zostaje przerwane. Informacja o błędzie zostaje wyświetlona w konsoli.
 *
 * @returns {PLUGIN_NAME[]} Nazwy zainicjowanych pluginów
 */
export const embedPlugins = async (): Promise<PLUGIN_NAME[]> => {
    const { UANC } = window.Inpl.Ad;

    const pluginsNamesToEmbed = getPluginsNamesToEmbed();

    if (!pluginsNamesToEmbed.includes(PLUGIN_NAME.TESTER)) {
        window.Inpl.Ad.services = {
            ...window.Inpl.Ad.services,
            TesterService: {
                isEnabled: false,
            },
            resolved: false,
        };
        UANC?.setState({ TesterServiceState: STATE_SERVICE.REJECTED });
    }

    const pluginPromises = await Promise.all(
        pluginsNamesToEmbed.map(async (pluginName) => {
            return (async () => {
                const plugin = await import(/* webpackChunkName: "[request]" */ './' + pluginName + '/' + pluginName);
                return await plugin[`process${pluginName}`]();
            })().catch((error) => {
                UANC instanceof DebugUANC &&
                    UANC.warn(`[embedPlugins] Problem with Embedded plugin. ${error instanceof Error ? error.message : error}`);
            });
        })
    );
    const loadedPlugins = pluginPromises.filter((pluginName) => pluginName);
    UANC instanceof DebugUANC && UANC.info('Embedded plugins', loadedPlugins.length ? loadedPlugins.join(', ') : '---');
    return loadedPlugins;
};
